// ============================================================
// 旧SPA操作防止（U-6 / docs/design_stale_spa_guard.md）UI
//
// アプリ root に常駐し、二段構えで stale 版を扱う:
//   1. 読み取り中: 上部に薄い更新バナー（閉じる可・即時リロードはしない）
//   2. 書き込み前: ブロックモーダル（api.ts の mutating guard が blocking を立てる）
// どちらも「ブラウザのリロードボタンを探させず」TAIARI UI 内の「更新する」で
// location.reload() し、同一 URL のまま最新版を読み直す。
//
// dev / build id 未設定では store が常に unknown を返すため、バナー・モーダルとも
// 描画されない（実質 no-op）。
// ============================================================

import { useEffect } from 'react'
import { useLocation } from 'react-router-dom'
import { RefreshCw, X } from 'lucide-react'
import { useAppVersionStore } from '@/stores/appVersionStore'

export default function StaleAppVersionBoundary() {
  const status = useAppVersionStore((s) => s.status)
  const blocking = useAppVersionStore((s) => s.blocking)
  const dismissedBuildId = useAppVersionStore((s) => s.dismissedBuildId)
  const check = useAppVersionStore((s) => s.check)
  const dismissBanner = useAppVersionStore((s) => s.dismissBanner)
  const reloadNow = useAppVersionStore((s) => s.reloadNow)
  const location = useLocation()

  // 起動時 + 復帰時（focus / visibilitychange）は force check で最新を取りに行く。
  // 長時間放置・スリープ復帰・別アプリ復帰という stale が実際に起きる経路をここで拾う。
  useEffect(() => {
    check(true)
    const onFocus = () => check(true)
    const onVisible = () => {
      if (document.visibilityState === 'visible') check(true)
    }
    window.addEventListener('focus', onFocus)
    document.addEventListener('visibilitychange', onVisible)
    return () => {
      window.removeEventListener('focus', onFocus)
      document.removeEventListener('visibilitychange', onVisible)
    }
  }, [check])

  // 画面遷移後の穏やかなチェック（TTL 内ならキャッシュを使う）
  useEffect(() => {
    check(false)
  }, [location.pathname, check])

  const isStale = status?.state === 'stale'
  const bannerVisible =
    isStale && !blocking && dismissedBuildId !== (status?.latestBuildId ?? null)

  return (
    <>
      {bannerVisible && (
        <div
          role="status"
          className="w-full bg-slate-800 text-white px-4 py-2 flex items-center justify-center gap-3
                     text-xs sm:text-sm select-none"
        >
          <span className="min-w-0 truncate font-medium">新しいバージョンがあります</span>
          <button
            type="button"
            onClick={reloadNow}
            className="shrink-0 inline-flex items-center gap-1 rounded bg-white px-2.5 py-1
                       text-slate-900 font-bold whitespace-nowrap hover:bg-neutral-100"
          >
            <RefreshCw size={13} />
            更新する
          </button>
          <button
            type="button"
            onClick={dismissBanner}
            aria-label="閉じる"
            className="shrink-0 rounded p-1 text-white/80 hover:bg-white/15 hover:text-white"
          >
            <X size={15} />
          </button>
        </div>
      )}

      {blocking && (
        <div
          className="fixed inset-0 z-[60] flex items-center justify-center bg-black/40 px-4"
          role="dialog"
          aria-modal="true"
          aria-labelledby="stale-app-title"
        >
          <div className="bg-white rounded-lg shadow-lg w-[440px] max-w-full flex flex-col">
            <div className="px-4 py-3 border-b border-neutral-200">
              <p id="stale-app-title" className="text-sm font-semibold text-neutral-800">
                画面を更新してください
              </p>
            </div>
            <div className="px-4 py-3 text-sm text-neutral-700">
              新しいバージョンがあります。安全に操作するため、画面を更新してからもう一度操作してください。
            </div>
            <div className="px-4 py-3 border-t border-neutral-200 flex justify-end">
              <button
                type="button"
                onClick={reloadNow}
                className="w-full sm:w-auto inline-flex items-center justify-center gap-1.5 px-4 py-2
                           rounded text-sm font-medium whitespace-nowrap bg-primary hover:bg-primary/90 text-white"
              >
                <RefreshCw size={15} />
                更新する
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  )
}
